<template>
  <div class="min-h-screen bg-gray-50 flex flex-col justify-center py-12 sm:px-6 lg:px-8">
    <!-- Header avec logo -->
    <div class="sm:mx-auto sm:w-full sm:max-w-md">
      <div class="flex justify-center">
        <div
          class="w-12 h-12 bg-gradient-to-br from-blue-600 to-blue-700 rounded-xl flex items-center justify-center shadow-lg">
          <svg class="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
              d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
          </svg>
        </div>
      </div>
      <h1 class="mt-4 text-center text-xl font-semibold text-gray-900">
        Anamnesis
      </h1>
    </div>

    <!-- Contenu principal -->
    <div class="mt-8 sm:mx-auto sm:w-full sm:max-w-md">
      <div class="bg-white py-8 px-4 shadow-sm sm:rounded-lg sm:px-10 border border-gray-200">

        <!-- Mode Login -->
        <template v-if="currentMode === 'login'">
          <LoginForm @submit="handleLogin" @forgot-password="switchMode('forgot-password')"
            @switch-to-register="switchMode('register')" />

          <!-- Connexion SSO Keycloak -->
          <div v-if="authStore.isKeycloakEnabled" class="mt-6">
            <div class="relative">
              <div class="absolute inset-0 flex items-center">
                <div class="w-full border-t border-gray-300" />
              </div>
              <div class="relative flex justify-center text-sm">
                <span class="px-2 bg-white text-gray-500">ou</span>
              </div>
            </div>
            <div class="mt-6">
              <button
                type="button"
                :disabled="authStore.isLoggingIn"
                class="w-full flex justify-center items-center gap-2 py-2 px-4 border border-gray-300 rounded-md shadow-sm bg-white text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                @click="handleKeycloakLogin"
              >
                <svg class="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M12 1L3 5v6c0 5.55 3.84 10.74 9 12 5.16-1.26 9-6.45 9-12V5l-9-4z"/>
                </svg>
                Se connecter avec Keycloak
              </button>
            </div>
          </div>
        </template>

        <!-- Mode Register -->
        <RegisterForm v-else-if="currentMode === 'register'" ref="registerFormRef" @submit="handleRegister"
          @switch-to-login="switchMode('login')" @show-terms="showTermsModal = true"
          @show-privacy="showPrivacyModal = true" />

        <!-- Mode Forgot Password -->
        <ForgotPasswordForm v-else-if="currentMode === 'forgot-password'" @submit="handleForgotPassword"
          @back-to-login="switchMode('login')" @switch-to-register="switchMode('register')" />

      </div>

   
    </div>



    <!-- Modals -->
    <TermsModal v-model="showTermsModal" />
    <PrivacyModal v-model="showPrivacyModal" />
    <HelpModal v-model="showHelpModal" />
  </div>
</template>

<script setup lang="ts">
import ForgotPasswordForm from '@/components/auth/ForgotPasswordForm.vue';
import HelpModal from '@/components/auth/HelpModal.vue';
import LoginForm from '@/components/auth/LoginForm.vue';
import PrivacyModal from '@/components/auth/PrivacyModal.vue';
import RegisterForm from '@/components/auth/RegisterForm.vue';
import TermsModal from '@/components/auth/TermsModal.vue';
import { useAuthStore } from '@/stores/auth';
import type { LoginRequest, RegisterRequest } from '@/types/auth';
import { nextTick, onMounted, ref, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';

type AuthMode = 'login' | 'register' | 'forgot-password';

const route = useRoute();
const router = useRouter();
const authStore = useAuthStore();

// État local
const currentMode = ref<AuthMode>('login');
const showTermsModal = ref(false);
const showPrivacyModal = ref(false);
const showHelpModal = ref(false);

// Références aux composants
const registerFormRef = ref<InstanceType<typeof RegisterForm> | null>(null);

// Gestion des modes
const switchMode = (mode: AuthMode): void => {
  currentMode.value = mode;

  // Mettre à jour l'URL sans recharger la page
  const newQuery = { ...route.query };
  if (mode !== 'login') {
    newQuery.mode = mode;
  } else {
    delete newQuery.mode;
  }

  router.replace({
    name: route.name || 'auth',
    query: newQuery
  });
};

// Handlers des formulaires
const handleLogin = async (credentials: LoginRequest): Promise<void> => {
  try {
    await authStore.login(credentials);

    // Attendre un tick pour que la réactivité se propage
    await nextTick();

    // Redirection après connexion réussie
    const redirectTo = route.query.redirect as string || '/dashboard';
    await router.push(redirectTo);

  } catch (error) {
    // L'erreur est gérée par le composant LoginForm avec les notifications
    console.error('Erreur de connexion:', error);
  }
};

const handleRegister = async (userData: RegisterRequest): Promise<void> => {
  try {
    const response = await authStore.register(userData);

    // Arrêter le loading
    registerFormRef.value?.stopSubmitting();

    // Afficher un message de succès selon la réponse
    if (response.email_sent) {
      // Email de validation envoyé
      registerFormRef.value?.setSuccessMessage('Inscription réussie ! Vérifiez votre email pour valider votre compte.');
      switchMode('login');
    } else {
      // Compte créé et validé automatiquement
      registerFormRef.value?.setSuccessMessage('Inscription réussie ! Vous pouvez maintenant vous connecter.');
      switchMode('login');
    }

  } catch (error) {
    // Propager l'erreur vers le composant RegisterForm
    console.error('Erreur d\'inscription:', error);

    // Appeler la méthode showError du RegisterForm
    registerFormRef.value?.showError(error);
  }
};

const handleForgotPassword = async (email: string): Promise<void> => {
  try {
    await authStore.requestPasswordReset(email);

    // Afficher un message de succès
    // Le composant ForgotPasswordForm gère l'affichage du message

  } catch (error) {
    // L'erreur est gérée par le composant ForgotPasswordForm avec les notifications
    console.error('Erreur de réinitialisation:', error);
  }
};

const handleKeycloakLogin = async (): Promise<void> => {
  try {
    await authStore.loginWithKeycloak();
  } catch (error) {
    console.error('Erreur de connexion Keycloak:', error);
  }
};

// Initialisation
onMounted(() => {
  // Vérifier si l'utilisateur est déjà connecté
  if (authStore.isAuthenticated) {
    const redirectTo = route.query.redirect as string || '/';
    router.push(redirectTo);
    return;
  }

  // Déterminer le mode initial depuis l'URL
  const modeFromQuery = route.query.mode as string;
  if (modeFromQuery && ['register', 'forgot-password'].includes(modeFromQuery)) {
    currentMode.value = modeFromQuery as AuthMode;
  }
});

// Watcher pour les changements de route
watch(() => route.query.mode, (newMode) => {
  if (newMode && ['register', 'forgot-password'].includes(newMode as string)) {
    currentMode.value = newMode as AuthMode;
  } else {
    currentMode.value = 'login';
  }
});
</script>
